import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronsLeft,
  ChevronsRight,
  Sparkles,
  Crown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SidebarSection } from "./SidebarSection";
import { SidebarNavItem } from "./SidebarNavItem";
import type { SidebarSectionConfig } from "./userSidebarConfig";

const STORAGE_KEY = "premium-sidebar:collapsed";

export type SidebarVariant = "user" | "admin" | "instructor";

interface SidebarShellProps {
  variant: SidebarVariant;
  sections: SidebarSectionConfig[];
  collapsed: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
  /** Hide chrome (logo, badge, footer) when used inside a mobile sheet */
  inSheet?: boolean;
}

const variantBadgeMap: Record<SidebarVariant, { labelKey: string; sublabelKey: string }> = {
  user: { labelKey: "sidebar.badge.user.label", sublabelKey: "sidebar.badge.user.sublabel" },
  admin: { labelKey: "sidebar.badge.admin.label", sublabelKey: "sidebar.badge.admin.sublabel" },
  instructor: {
    labelKey: "sidebar.badge.instructor.label",
    sublabelKey: "sidebar.badge.instructor.sublabel",
  },
};

export function SidebarShell({
  variant,
  sections,
  collapsed,
  onToggle,
  onNavigate,
  inSheet,
}: SidebarShellProps) {
  const { t } = useTranslation();
  const badge = variantBadgeMap[variant];
  const showExpanded = !collapsed || inSheet;

  return (
    <TooltipProvider delayDuration={150}>
      <aside
        className={cn(
          "relative flex h-full flex-col border-r border-white/10 bg-card/40 backdrop-blur-2xl",
          "transition-[width] duration-300 ease-out",
          inSheet ? "w-full" : collapsed ? "w-[72px]" : "w-[252px]"
        )}
        aria-label="Primary navigation"
      >
        {/* Logo - text-only wordmark, no symbol */}
        <div
          className={cn(
            "flex items-center border-b border-white/5 py-4",
            showExpanded ? "px-4" : "px-2 justify-center"
          )}
        >
          <Link
            to="/"
            className="group flex items-center min-w-0"
            aria-label="AICAVITE"
          >
            {showExpanded ? (
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="bg-gradient-primary bg-clip-text text-base font-semibold tracking-[0.22em] text-transparent truncate group-hover:opacity-90 transition-opacity">
                  AICAVITE
                </span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-foreground/40 truncate mt-0.5">
                  {t("sidebar.tagline")}
                </span>
              </div>
            ) : (
              <span
                className="bg-gradient-primary bg-clip-text text-sm font-bold tracking-[0.15em] text-transparent group-hover:opacity-90 transition-opacity"
                aria-hidden="true"
              >
                AI
              </span>
            )}
          </Link>
        </div>

        {/* Variant badge card (or icon-only chip when collapsed) */}
        {showExpanded ? (
          <div className="px-3 pt-3">
            <div className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/[0.10] to-secondary/[0.05] p-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary/20">
                  <Crown className="h-4 w-4 text-primary" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-foreground truncate leading-tight">
                    {t(badge.labelKey)}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-foreground/50 truncate mt-0.5">
                    {t(badge.sublabelKey)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-2 pt-3 flex justify-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-primary/20 bg-primary/15">
                  <Crown className="h-4 w-4 text-primary" aria-hidden="true" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs">
                {t(badge.labelKey)} · {t(badge.sublabelKey)}
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Nav scroll area */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 pb-4 pt-2">
          {sections.map((section) => (
            <SidebarSection
              key={section.labelKey}
              label={t(section.labelKey)}
              collapsed={!showExpanded}
            >
              {section.items.map((item) => (
                <SidebarNavItem
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={t(item.labelKey)}
                  end={item.end}
                  collapsed={!showExpanded}
                  onNavigate={onNavigate}
                />
              ))}
            </SidebarSection>
          ))}
        </nav>

        {/* AI assistant card */}
        {(() => {
          const ctaHref =
            variant === "admin"
              ? "/admin/ai-quiz-tools"
              : variant === "instructor"
                ? "/learning-analytics"
                : "/dashboard";
          return (
            <div className={cn("border-t border-white/5 p-3", !showExpanded && "px-2")}>
              {showExpanded ? (
                <div className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/[0.10] to-secondary/[0.05] p-3">
                  <div className="mb-2 flex items-center gap-2 min-w-0">
                    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-primary/20">
                      <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-foreground truncate">
                        {t("sidebar.assistant.title")}
                      </div>
                      <div className="text-[10px] text-foreground/50 truncate">
                        {t("sidebar.assistant.subtitle")}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="default"
                    className="w-full text-xs h-8"
                    asChild
                    onClick={onNavigate}
                  >
                    <Link to={ctaHref}>{t("sidebar.assistant.cta")}</Link>
                  </Button>
                </div>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={ctaHref}
                      onClick={onNavigate}
                      className="mx-auto flex h-9 w-9 items-center justify-center rounded-lg bg-primary/20 hover:bg-primary/30 transition-colors"
                      aria-label={t("sidebar.assistant.cta")}
                    >
                      <Sparkles className="h-4 w-4 text-primary" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-xs">
                    {t("sidebar.assistant.cta")}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        })()}

        {/* Theme toggle (only when expanded) */}
        {showExpanded && (
          <div className="border-t border-white/5 px-3 py-2 flex items-center justify-center">
            <ThemeToggle />
          </div>
        )}

        {/* Collapse toggle (desktop only) */}
        {!inSheet && (
          <button
            type="button"
            onClick={onToggle}
            className={cn(
              "absolute -right-3 top-16 z-20 flex h-6 w-6 items-center justify-center rounded-full",
              "border border-white/10 bg-card backdrop-blur-xl shadow-md",
              "text-foreground/70 hover:text-foreground hover:bg-card transition-all"
            )}
            aria-label={collapsed ? t("sidebar.expand") : t("sidebar.collapse")}
          >
            {collapsed ? (
              <ChevronsRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronsLeft className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </aside>
    </TooltipProvider>
  );
}

export function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((c) => !c), []);
  return [collapsed, toggle];
}
